import { useEffect, useRef, useState } from 'preact/hooks';
import { api, session } from '../api';
import { Checkbox, ErrorText } from '../ui';
import { DISCORD_URL } from '../game/ui/controls';

export function Login(props: { onDone: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState(session.email ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [adult, setAdult] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setError(null);
    if (mode === 'register') {
      if (password !== confirm) return setError('As senhas não conferem.');
      if (!adult) return setError('Confirme que você tem 18 anos ou mais.');
    }
    setBusy(true);
    try {
      const r = mode === 'login' ? await api.login(email, password) : await api.register(email, password, adult);
      session.save(r);
      props.onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const [show, setShow] = useState(false);
  // Login com Google (Google Identity Services): o botão só aparece se o servidor tiver um Client ID configurado.
  const gBox = useRef<HTMLDivElement>(null);
  const [gPending, setGPending] = useState<string | null>(null);   // credencial esperando a confirmação de 18+
  const [gAdult, setGAdult] = useState(false);
  const google = async (credential: string, adultOk: boolean) => {
    setError(null); setBusy(true);
    try { session.save(await api.googleLogin(credential, adultOk)); props.onDone(); }
    catch (err: any) { if (err.status === 428) setGPending(credential); else setError(err.message); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    let alive = true;
    api.config().then((c) => {
      if (!alive || !c.googleClientId) return;
      const draw = () => {
        const g = (window as any).google?.accounts?.id;
        if (!g || !gBox.current || !alive) return;
        g.initialize({ client_id: c.googleClientId, callback: (r: { credential: string }) => void google(r.credential, false), ux_mode: 'popup' });
        g.renderButton(gBox.current, { theme: 'filled_black', size: 'large', shape: 'pill', text: 'continue_with', locale: 'pt-BR', width: 280 });
      };
      if ((window as any).google?.accounts?.id) return draw();
      const sc = document.createElement('script');
      sc.src = 'https://accounts.google.com/gsi/client'; sc.async = true; sc.onload = draw;
      document.head.appendChild(sc);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div class="nx-hero">
      <div class="nx-side">
        <img class="nx-logo" src="/ui/logo.webp" alt="Baiak: Noxara" />
        <form onSubmit={submit} class="nx-panel">
          <i class="nx-orn tl" /><i class="nx-orn tr" /><i class="nx-orn bl" /><i class="nx-orn br" /><i class="nx-orn top" />
          {mode === 'register' && <h2 class="nx-title">Criar conta</h2>}
          <label class="nx-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5 6c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5" fill="none" stroke="currentColor" stroke-width="1.3" /></svg>
            <input type="email" placeholder="E-mail" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} autoComplete="email" autoFocus required />
          </label>
          <label class="nx-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="1.3" /><path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" stroke-width="1.3" /></svg>
            <input type={show ? 'text' : 'password'} placeholder="Senha" value={password} onInput={(e) => setPassword((e.target as HTMLInputElement).value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required />
            <button type="button" class="nx-eye" title={show ? 'Esconder senha' : 'Mostrar senha'} onClick={() => setShow(!show)}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z" fill="none" stroke="currentColor" stroke-width="1.3" /><circle cx="8" cy="8" r="2" fill="currentColor" />{show && <path d="M2 14 14 2" stroke="currentColor" stroke-width="1.3" />}</svg>
            </button>
          </label>
          {mode === 'register' && (
            <>
              <label class="nx-input">
                <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="1.3" /><path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" stroke-width="1.3" /></svg>
                <input type={show ? 'text' : 'password'} placeholder="Repita a senha" value={confirm} onInput={(e) => setConfirm((e.target as HTMLInputElement).value)} autoComplete="new-password" required />
              </label>
              <Checkbox checked={adult} onChange={setAdult}>Tenho 18 anos ou mais e aceito os termos de uso</Checkbox>
            </>
          )}
          <ErrorText error={error} />
          <button type="submit" class="nx-btn" disabled={busy}>{busy ? '...' : mode === 'login' ? 'Entrar' : 'Criar conta'}</button>
          <div class="nx-links">
            <a href="#" onClick={(e) => { e.preventDefault(); setError(null); setMode(mode === 'login' ? 'register' : 'login'); }}>
              {mode === 'login' ? 'Criar conta' : 'Já tenho conta'}
            </a>
          </div>
          <div class="nx-google" ref={gBox} />
          <div class="nx-rule"><i /></div>
        </form>
        {gPending && (
          <div class="nx-panel nx-gconfirm">
            <h2 class="nx-title">Criar conta com Google</h2>
            <p class="small">Ainda não existe uma conta do jogo com esse e-mail. Para criar, confirme:</p>
            <Checkbox checked={gAdult} onChange={setGAdult}>Tenho 18 anos ou mais e aceito os termos de uso</Checkbox>
            <div class="row between">
              <button type="button" class="nx-btn ghost" onClick={() => setGPending(null)}>Cancelar</button>
              <button type="button" class="nx-btn" disabled={!gAdult || busy} onClick={() => { const c = gPending; setGPending(null); void google(c, true); }}>Criar conta</button>
            </div>
          </div>
        )}
        <a class="nx-discord" href={DISCORD_URL} target="_blank" rel="noopener">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="#5865F2" /><rect x="5" y="7" width="14" height="10" rx="4.5" fill="#fff" /><circle cx="9.6" cy="11.6" r="1.4" fill="#5865F2" /><circle cx="14.4" cy="11.6" r="1.4" fill="#5865F2" /></svg>
          Entre no nosso Discord: avisos, suporte e wiki
        </a>
        <small class="nx-foot">Jogo para maiores de 18 anos · <a href="/termos.html" target="_blank">Termos de uso</a> · <a href="/privacidade.html" target="_blank">Privacidade</a></small>
      </div>
    </div>
  );
}
